import type { Movie } from "@/mocks/movies";
import { movies } from "@/mocks/movies";

export const ALL = "全部";

export function getMovieById(id: string | undefined): Movie | undefined {
  if (!id) return undefined;
  return movies.find((movie) => movie.id === id);
}

export function getGenres(): string[] {
  return Array.from(new Set(movies.flatMap((movie) => movie.genres)));
}

export function getRegions(): string[] {
  return Array.from(new Set(movies.map((movie) => movie.region)));
}

export interface FilterOptions {
  type?: string;
  genre?: string;
  region?: string;
  year?: string;
}

export function filterMovies(options: FilterOptions): Movie[] {
  const { type, genre, region, year } = options;
  return movies.filter((movie) => {
    if (type && type !== ALL && movie.type !== type) return false;
    if (genre && genre !== ALL && !movie.genres.includes(genre)) return false;
    if (region && region !== ALL && movie.region !== region) return false;
    if (year && year !== ALL) {
      if (year === "近5年") {
        if (movie.year < 2020) return false;
      } else if (String(movie.year) !== year) {
        return false;
      }
    }
    return true;
  });
}

export type SortKey = "update" | "heat" | "rating";

export function sortMovies(list: Movie[], key: SortKey): Movie[] {
  const sorted = [...list];
  if (key === "update") {
    sorted.sort((a, b) => b.updateTime.localeCompare(a.updateTime));
  } else if (key === "heat") {
    sorted.sort((a, b) => b.heat - a.heat);
  } else if (key === "rating") {
    sorted.sort((a, b) => b.rating - a.rating);
  }
  return sorted;
}

export function searchMovies(keyword: string): Movie[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return movies
    .filter((movie) => {
      const haystack = [
        movie.title,
        movie.type,
        movie.region,
        String(movie.year),
        movie.director,
        ...movie.genres,
        ...movie.tags,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    })
    .sort((a, b) => {
      const aTitle = a.title.toLowerCase().includes(q) ? 1 : 0;
      const bTitle = b.title.toLowerCase().includes(q) ? 1 : 0;
      return bTitle - aTitle;
    });
}

export function getRelated(movie: Movie, limit = 4): Movie[] {
  return movies
    .filter((item) => item.id !== movie.id)
    .map((item) => {
      const genreOverlap = item.genres.filter((g) =>
        movie.genres.includes(g)
      ).length;
      const tagOverlap = item.tags.filter((t) => movie.tags.includes(t)).length;
      const typeSame = item.type === movie.type ? 1 : 0;
      return { item, score: genreOverlap * 2 + tagOverlap * 2 + typeSame };
    })
    .sort((a, b) => b.score - a.score || b.item.heat - a.item.heat)
    .slice(0, limit)
    .map((entry) => entry.item);
}

export function paginate<T>(list: T[], page: number, pageSize: number) {
  const totalPages = Math.max(1, Math.ceil(list.length / pageSize));
  const current = Math.min(Math.max(1, page), totalPages);
  const start = (current - 1) * pageSize;
  return {
    items: list.slice(start, start + pageSize),
    current,
    totalPages,
    total: list.length,
  };
}

export function getYearOptions(): string[] {
  const years = Array.from(new Set(movies.map((movie) => movie.year))).sort(
    (a, b) => b - a
  );
  return [ALL, "近5年", ...years.map(String)];
}